import { FormLayout, Modal, TextField, Form, Text, Checkbox, Icon, Select, SkeletonBodyText } from '@shopify/polaris';
import { useCallback, useEffect, useRef, useState } from 'react';
import helpers from 'helpers';
import { lengthLessThan, notEmpty, useField, useForm } from '@shopify/react-form';
import __ from 'languages/index';
import { useUpdateTripTime, useMutateCreateTripTime, useMutateGetTripTime, TypedTrip_time } from 'queries/trip_time.query';
import { useNotification } from 'context/NotificationContext';
import { ClockIcon } from '@shopify/polaris-icons';
import { useGetSeatings } from 'queries/seating.query';

export default function TripTimeModal({
  onClose,
  show,
  currentTripScheduleID,
  currentTripTime,
}: {
  onClose: Function;
  show: boolean;
  currentTripScheduleID: string;
  currentTripTime: string;
}) {
  const { mutateAsync: createEntity, isPending: creating, isSuccess: creatingSuccess } = useMutateCreateTripTime();
  const [currentTripTimeData, setCurrentTripTimeData] = useState<TypedTrip_time>(null);
  const { mutateAsync: getTripTime } = useMutateGetTripTime();
  const { mutateAsync: updateEntity, isSuccess: updateSuccessful } = useUpdateTripTime();

  const [seatingOptions, setSeatingOptions] = useState([]);
  const { data: seatingTemplateData, isLoading: seatingLoading } = useGetSeatings({
    limit: 20,
    sort: 'createdAt:desc',
  });

  useEffect(() => {
    if (!helpers.isEmpty(seatingTemplateData)) {
      let { body } = seatingTemplateData;
      let totalResult = [
        {
          label: 'Chọn một loại hình',
          value: '',
        },
      ];
      for (let seating of body) {
        totalResult.push({
          label: seating.seating_name,
          value: seating.id,
        });
      }

      setSeatingOptions(totalResult);
    }
  }, [seatingTemplateData]);

  useEffect(() => {
    if (currentTripTime) {
      getTripTime(currentTripTime)
        .then((data) => {
          setCurrentTripTimeData(data);
        })
        .catch((e) => {});
    }
  }, [show, currentTripTime]);

  const useFields = {
    time: useField<string>({
      value: currentTripTimeData?.time,
      validates: [
        notEmpty('Không được để trống trường này.'),
        lengthLessThan(250, 'No more than 250 characters.'),
        (inputValue) => {
          if (!helpers.isValidTime(inputValue)) {
            return 'Định dạng giờ hh:mm không đúng!';
          }
        },
      ],
    }),

    price: useField<number>({
      value: currentTripTimeData?.price || 0,
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),

    shipping_price: useField<number>({
      value: currentTripTimeData?.shipping_price || 0,
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),

    seating_id: useField<string>({ value: currentTripTimeData?.seating_id || '', validates: [notEmpty('Không được để trống trường này!')] }),
  };

  const { fields, submit, dirty, reset, makeClean } = useForm({
    fields: useFields,

    async onSubmit(values) {
      try {
        // create new

        if (!currentTripTime) {
          await createEntity({
            time: values.time,
            price: helpers.parseNumeric(values.price),
            shipping_price: helpers.parseNumeric(values.shipping_price),
            trip_schedule_id: String(currentTripScheduleID),
            seating_id: values.seating_id ?? null,
          });
        } else {
          await updateEntity({
            id: currentTripTime,
            time: values.time,
            price: helpers.parseNumeric(values.price),
            shipping_price: helpers.parseNumeric(values.shipping_price),
            trip_schedule_id: String(currentTripScheduleID),
            seating_id: values.seating_id ?? null,
          });
        }
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });
  /* End form */

  /** Khi chọn một seating Template, hơi dài dòng */
  const changeSeatingTemplateCallback = useCallback(
    (idSelected: any) => {
      fields.seating_id.onChange(idSelected);
    },
    [seatingTemplateData]
  );

  const { addNotification } = useNotification();

  useEffect(() => {
    if (creatingSuccess) {
      onClose();
      addNotification('info', 'Tạo giờ thành công!');
    }
  }, [creatingSuccess]);
  useEffect(() => {
    if (updateSuccessful) {
      onClose();
      addNotification('info', 'Cập nhật giờ thành công!');
    }
  }, [updateSuccessful]);

  /** clear cái form trước khi đóng lại ... */
  useEffect(() => {
    return () => {
      setCurrentTripTimeData(null);
      helpers.resetForm(fields);
      reset();
      makeClean();
      console.log('CLear before close ...');
    };
  }, [show]);

  /** Tự động format giờ */
  const prevDigitsRef = useRef('');
  const handleTimeChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    const prevDigits = prevDigitsRef.current;
    const isAdding = digits.length > prevDigits.length;

    let formatted = raw;

    if (isAdding) {
      // chỉ format khi đang gõ thêm
      formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
    } else {
      // nếu đang xóa: giữ nguyên dấu ':' nếu nó đang có trong raw
      if (raw.includes(':')) {
        formatted = raw;
      } else {
        // fallback: format lại theo digits để tránh lộn xộn
        formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
      }
    }

    prevDigitsRef.current = digits;
    return formatted;
  };

  const Actual_page = (
    <Modal
      open={show}
      onClose={() => onClose()}
      title={'Tạo giờ chạy'}
      primaryAction={{
        content: 'Tạo',
        disabled: !dirty,
        loading: creating,
        onAction: submit,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: () => onClose(),
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <TextField
              autoFocus
              autoComplete="off"
              label="Giờ chạy"
              placeholder=""
              suffix="hh:mm"
              maxLength={5}
              prefix={<Icon source={ClockIcon} />}
              {...fields.time}
              value={fields.time.value}
              onChange={(v) => fields.time.onChange(handleTimeChange(v))}
              clearButton
              onClearButtonClick={() => fields.time.onChange('')}
            />

            <FormLayout.Group>
              <TextField
                label="Giá khách (VND)"
                autoComplete="off"
                {...fields.price}
                value={helpers.formatNumberWithCommas(fields.price.value)}
                onChange={(a) => fields.price.onChange(helpers.parseNumeric(a))}
              />
              <TextField
                label="Giá gửi hàng (VND)"
                autoComplete="off"
                {...fields.shipping_price}
                value={helpers.formatNumberWithCommas(fields.shipping_price.value)}
                onChange={(a) => fields.shipping_price.onChange(helpers.parseNumeric(a))}
              />
            </FormLayout.Group>

            {/* 4. Thay thế TextField bằng Select */}
            {seatingLoading ? (
              <SkeletonBodyText />
            ) : (
              <Select
                label="Loại hình vận chuyển?"
                requiredIndicator
                options={seatingOptions}
                onChange={changeSeatingTemplateCallback}
                value={fields.seating_id.value} // Giá trị của Select là ID (lấy từ trường _geo)
                error={fields.seating_id.error} // Hiển thị lỗi từ trường _geo
              />
            )}

            <Text as="p" tone="subdued">
              *Có thể ghi đè giá mặc định hoặc loại hình vận chuyển của giờ chạy này.
            </Text>
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );

  return <>{Actual_page}</>;
}

